import type { NaicsCodeId } from "@inrev/common";
import { useCreateAccount } from "@portal/api/account/agent";
import { SectionNavLayout } from "@portal/components/multiform/SectionNavLayout";
import type { StepConfig } from "@portal/components/multiform/types";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { z } from "zod";
import type { BondFormData } from "../../../../types";
import {
	BondRequestCreateContext,
	type PrincipalCompanyOption,
} from "../../request/create/BondRequestCreateView";
import { transformBondFormForSubmission } from "../../request/create/schema";
import {
	AccountCreateFormData,
	accountFormValidationSchema,
	type CombinedFormData,
	getAccountFormValidationSchema,
	getCombinedFormValidationSchema,
	getDefaultAccountFormData,
	getDefaultCombinedFormData,
	toAccountCreateRequest,
} from "./schema";
import { additionalInfoStepConfig } from "./steps/additional-info/AdditionalInfo.config";
import { bondStepConfig } from "./steps/bond/Bond.config";
import { detailsStepConfig } from "./steps/details/Details.config";
import { financialsStepConfig } from "./steps/financials/Financials.config";
import { historyStepConfig } from "./steps/history/History.config";
import { useClearContractHistoryFields } from "./steps/history/useClearContractHistoryFields";
import { useAccountSubmissionAnalytics } from "./useAccountSubmissionAnalytics";

const defaultBondFormData: BondFormData = {
	templateId: "",
	upload: { type: "", files: [] },
};

const prequalSteps: StepConfig<AccountCreateFormData>[] = [
	detailsStepConfig,
	historyStepConfig,
	financialsStepConfig,
	additionalInfoStepConfig,
];

// These step configs are typed as StepConfig<AccountCreateFormData> but are safely reusable here
// because they only access `account.*` fields. The cast is needed because React's FC/ComponentType
// types are invariant in P due to the legacy `propTypes` field (WeakValidationMap -> Validator -> nominalTypeHack).
const combinedSteps: StepConfig<CombinedFormData>[] = [
	detailsStepConfig as unknown as StepConfig<CombinedFormData>,
	historyStepConfig as unknown as StepConfig<CombinedFormData>,
	financialsStepConfig as unknown as StepConfig<CombinedFormData>,
	bondStepConfig,
	additionalInfoStepConfig as unknown as StepConfig<CombinedFormData>,
];

export const AccountCreateView = () => {
	const [searchParams] = useSearchParams();
	const isBondRequest = searchParams.get("type") === "bond-request";

	if (isBondRequest) {
		return <CombinedAccountCreateView />;
	}

	return <PrequalAccountCreateView />;
};

const PrequalAccountCreateView = () => {
	const navigate = useNavigate();
	const { createAccount, createAccountIsLoading } = useCreateAccount();
	const shouldAskPaymentAndPerformanceBonds = useIsCommercialSuretyEnabled();
	const schema = useMemo(
		() =>
			getAccountFormValidationSchema({
				requirePaymentAndPerformanceBonds: shouldAskPaymentAndPerformanceBonds,
			}),
		[shouldAskPaymentAndPerformanceBonds],
	);
	const schemaRef = useRef<z.ZodType>(accountFormValidationSchema);
	schemaRef.current = schema;

	const { sectionNavCallbacks, onAccountCreated, onSubmissionFailed } =
		useAccountSubmissionAnalytics<AccountCreateFormData>("prequal", schema, "full");

	const formMethods = useForm<AccountCreateFormData>({
		mode: "onBlur",
		resolver: (values, context, options) =>
			createFormResolver<AccountCreateFormData>(schemaRef.current)(values, context, options),
		defaultValues: getDefaultAccountFormData(),
	});
	useClearContractHistoryFields(formMethods);

	const handleSubmit = async (data: AccountCreateFormData) => {
		createAccount({
			data: toAccountCreateRequest(data),
			onSuccess: (accountId) => {
				onAccountCreated(accountId, data, data.account.modelSize);
				navigate(`/accounts/${accountId}`);
			},
			onError: onSubmissionFailed,
		});
	};

	return (
		<FormProvider {...formMethods}>
			<FormSchemaProvider value={schema}>
				<SectionNavLayout
					{...sectionNavCallbacks}
					formMethods={formMethods}
					steps={prequalSteps}
					confirmationMessage="Are you sure you want to submit?"
					isSubmitting={createAccountIsLoading}
					onSuccess={handleSubmit}
					trackingPrefix="account-agent-prequal-full"
				/>
			</FormSchemaProvider>
		</FormProvider>
	);
};

const CombinedAccountCreateView = () => {
	const navigate = useNavigate();
	const { createAccount, createAccountIsLoading } = useCreateAccount();

	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const schemaRef = useRef<z.ZodType>(
		getCombinedFormValidationSchema({
			minimalMode: isMinimalUIEnabled,
			requirePaymentAndPerformanceBonds: isCommercialSuretyEnabled,
			commercialSuretyEnabled: isCommercialSuretyEnabled,
		}),
	);

	const [bondFormData, setBondFormData] = useState<BondFormData>(defaultBondFormData);
	const bondFormRef = useRef(bondFormData);
	bondFormRef.current = bondFormData;

	const formMethods = useForm<CombinedFormData>({
		mode: "onBlur",
		resolver: (values, context, options) =>
			createFormResolver<CombinedFormData>(schemaRef.current)(values, context, options),
		defaultValues: getDefaultCombinedFormData(),
	});
	useClearContractHistoryFields(formMethods);

	const selectedBondType = formMethods.watch("bondType");
	const schema = useMemo(
		() =>
			getCombinedFormValidationSchema({
				minimalMode: isMinimalUIEnabled,
				requirePaymentAndPerformanceBonds: isCommercialSuretyEnabled,
				commercialSuretyEnabled: isCommercialSuretyEnabled,
				bondType: selectedBondType,
			}),
		[isMinimalUIEnabled, isCommercialSuretyEnabled, selectedBondType],
	);
	schemaRef.current = schema;
	const { sectionNavCallbacks, onAccountCreated, onSubmissionFailed } =
		useAccountSubmissionAnalytics<CombinedFormData>("combined", schema, "full");

	const accountNaicsCodes = useWatch({
		control: formMethods.control,
		name: "account.naicsCodes",
	}) as NaicsCodeId[] | undefined;

	const formCompanies = useWatch({
		control: formMethods.control,
		name: "account.details.companies",
	});

	const companies = useMemo((): PrincipalCompanyOption[] => {
		if (!formCompanies) return [];
		return formCompanies
			.filter((c) => c?.name)
			.map((c) => ({
				name: c.name,
				isPrimary: c.isPrimaryCompany === true,
			}));
	}, [formCompanies]);

	useEffect(() => {
		const bondForm = transformBondFormForSubmission(bondFormData);
		formMethods.setValue("quote.bondForm", bondForm ?? null);
	}, [bondFormData, formMethods]);

	const handleSubmit = async (data: CombinedFormData) => {
		const submissionData = {
			...data,
			aiEnabled: true as const,
			quote: data.quote
				? {
						...data.quote,
						bondForm: transformBondFormForSubmission(bondFormRef.current),
					}
				: data.quote,
		};
		createAccount({
			data: toAccountCreateRequest(submissionData),
			onSuccess: (accountId) => {
				onAccountCreated(accountId, submissionData, submissionData.account.modelSize);
				navigate(`/accounts/${accountId}`);
			},
			onError: onSubmissionFailed,
		});
	};

	return (
		<BondRequestCreateContext.Provider
			value={{ accountNaicsCodes, bondFormData, setBondFormData, companies }}
		>
			<FormProvider {...formMethods}>
				<FormSchemaProvider value={schema}>
					<SectionNavLayout
						{...sectionNavCallbacks}
						formMethods={formMethods}
						steps={combinedSteps}
						confirmationMessage="Are you sure you want to submit?"
						isSubmitting={createAccountIsLoading}
						onSuccess={handleSubmit}
						trackingPrefix="account-agent-combined-full"
					/>
				</FormSchemaProvider>
			</FormProvider>
		</BondRequestCreateContext.Provider>
	);
};
